import { useQuery, NetworkStatus, useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayoutV2 from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { EditRoute } from 'features/Routes/components/EditRoute'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { ISubmitFormInput } from 'features/Routes/views/Edit/types'
import { IService } from 'features/Services/components/NewServicesV2/components/ServicesForm/types'
import { GET_SERVICES_FOR_EDIT_ROUTE } from 'features/Services/queries-legacy'
import { CREATE_SERVICE_ITINERARIES_SCHEDULE, SCHEDULE_ROUTE_SERVICE_TRIPS } from 'features/Services/mutations-legacy'
import { mapItinerariesToItinerariesSchedule } from 'models/services'
import { canWrite } from 'features/Users/utils'
import {
  GTCreateRouteServiceItinerarySchedulePlaceReplacementInput,
  GTTPlaceSuggestionsResponse,
  GTTRoute
} from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { GET_ROUTE } from 'features/Routes/queries-legacy'
import { GET_PLACES_SUGGESTIONS } from 'features/Places/queries-legacy'
import { isEmpty } from 'lodash'

const messages = defineMessages({
  helmet: {
    id: 'routes.edit.helmet',
    defaultMessage: 'Edit Route | Nexbus Admin'
  }
})

const getPlaceReplacements = (
  oldRoute: GTTRoute,
  newRoute: GTTRoute
): GTCreateRouteServiceItinerarySchedulePlaceReplacementInput[] => {
  const placeReplacements: GTCreateRouteServiceItinerarySchedulePlaceReplacementInput[] = []
  oldRoute.stops.forEach((oldStop, stopIndex) => {
    const newStop = newRoute.stops[stopIndex]
    if (oldStop.id !== newStop.id) {
      placeReplacements.push({
        oldPlaceID: oldStop.id,
        newPlaceID: newStop.id
      })
    }
  })
  return placeReplacements
}

const EditRouteView = (): TSFixMe => {
  const navigate = useNavigate()
  const { routeId } = useParams()
  const intl = useIntl()

  const [unsavedServicesCodes, setUnsavedServicesCodes] = useState<string[]>([])

  const login = useLogin()

  const {
    loading: routeLoading,
    error: routeError,
    data: routeData,
    refetch: routeRefetch,
    networkStatus: routeNetworkStatus
  } = useQuery<{ troute: GTTRoute }>(GET_ROUTE, {
    notifyOnNetworkStatusChange: true,
    variables: {
      routeId
    }
  })
  const {
    data: placesData,
    loading: placesLoading,
    error: placesError,
    refetch: placesRefetch,
    networkStatus: placesNetworkStatus
  } = useQuery<{ tplaceSuggestions: GTTPlaceSuggestionsResponse }>(GET_PLACES_SUGGESTIONS, {
    skip: isEmpty(routeData?.troute.stops),
    variables: {
      input: {
        ids: routeData?.troute?.stops.map((stop) => stop.id)
      }
    }
  })

  const {
    loading: servicesLoading,
    error: servicesError,
    data: servicesData,
    refetch: servicesRefetch,
    networkStatus: servicesNetworkStatus
  } = useQuery(GET_SERVICES_FOR_EDIT_ROUTE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      routeId
    }
  })

  const [createServiceItineraries, { error: createItinerariesError, loading: createItinerariesLoading }] = useMutation(
    CREATE_SERVICE_ITINERARIES_SCHEDULE
  )
  const [scheduleServiceTrips, { loading: scheduleServiceTripsLoading, error: scheduleServiceTripsError }] =
    useMutation(SCHEDULE_ROUTE_SERVICE_TRIPS)

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)

    routeRefetch()
    servicesRefetch()
  }, [])

  if (!canWrite(FEATURES.ROUTE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ROUTE} />
  }

  let content

  const handleSubmit = async ({ route, services, isReplacingPlaces }: ISubmitFormInput): Promise<void> => {
    setUnsavedServicesCodes(services.map((service: IService): string => service.code))
    try {
      for (const service of services) {
        await createServiceItineraries({
          variables: {
            input: {
              id: service.id,
              itinerariesSchedule: mapItinerariesToItinerariesSchedule(service.itineraries).map((itinerarySchedule) => {
                delete itinerarySchedule.id
                return {
                  ...itinerarySchedule,
                  oldRouteID: routeId,
                  placeReplacements: isReplacingPlaces ? getPlaceReplacements(routeData?.troute as GTTRoute, route) : []
                }
              })
            }
          }
        })
        setUnsavedServicesCodes(unsavedServicesCodes.filter((code: string): boolean => code !== service.code))
        await scheduleServiceTrips({
          variables: {
            input: {
              id: service.id
            }
          }
        })
      }
      const origin = route.stops[0]
      const destination = route.stops[route.stops.length - 1]
      navigate(
        `/routes?highlightedRoute=${route.id}&origin=${origin.id}&destination=${destination.id}&companyId=${route.company?.id}`
      )
    } catch (error) {
      // Do nothing
    }
  }

  const loading =
    routeNetworkStatus === NetworkStatus.refetch ||
    routeLoading ||
    servicesNetworkStatus === NetworkStatus.refetch ||
    servicesLoading ||
    placesNetworkStatus === NetworkStatus.refetch ||
    placesLoading

  if (loading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (routeError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="routes.edit.error.description"
              defaultMessage="There was an error while loading your route"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button onClick={(): TSFixMe => routeRefetch()} kind="primary">
              <FormattedMessage id="routes.edit.error.action" defaultMessage="Reload your route" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (servicesError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="routes.edit.services.error.description"
              defaultMessage="There was an error while loading your services"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button onClick={(): TSFixMe => servicesRefetch()} kind="primary">
              <FormattedMessage id="routes.edit.services.error.action" defaultMessage="Reload your services" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (placesError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="routes.edit.places.error.description"
              defaultMessage="There was an error while loading your places"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button onClick={(): TSFixMe => placesRefetch()} kind="primary">
              <FormattedMessage id="routes.edit.places.error.action" defaultMessage="Reload your places" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    const loading = createItinerariesLoading || scheduleServiceTripsLoading
    let error
    if (createItinerariesError) {
      error = (
        <FormattedMessage
          id="routes.edit.error"
          defaultMessage="There was an error while changing the {unsavedServicesCount, plural,
        one {service}
        other {services}
      } {servicesCodes}"
          values={{
            servicesCodes: unsavedServicesCodes.join(', '),
            unsavedServicesCount: unsavedServicesCodes.length
          }}
        />
      )
    } else if (scheduleServiceTripsError) {
      error = (
        <FormattedMessage
          id="routes.edit.schedule.error"
          defaultMessage="There was an error while scheduling your trips"
        />
      )
    }
    const route = {
      ...routeData?.troute,
      stops: routeData?.troute.stops.map((stop) => {
        const place = placesData?.tplaceSuggestions?.items.find((place) => place.id === stop.id)
        return {
          id: stop.id,
          name: stop.name,
          location: {
            latitude: place?.location?.latitude || '',
            longitude: place?.location?.longitude || ''
          }
        }
      })
    }
    return (
      <FormLayoutV2.Container>
        <EditRoute
          onSubmit={handleSubmit}
          route={route}
          services={servicesData?.servicesWithPagination?.items || []}
          error={error}
          loading={loading}
        />
      </FormLayoutV2.Container>
    )
  }

  return (
    <FormLayoutV2.Container>
      <FormLayoutV2.Content>
        <Heading.H3>
          <FormattedMessage id="routes.edit.title" defaultMessage="Change route" />
        </Heading.H3>
      </FormLayoutV2.Content>
      {content}
    </FormLayoutV2.Container>
  )
}

export default withLastUrl(EditRouteView)
